/** The tour draft → the existing PATCH (plan §3 "Compile", D3). Runs at save and at
 *  preview time only, never per keystroke.
 *
 *  1. Only touched top-level keys are sent (key-present semantics).
 *  2. `context` sends only the touched keys; an empty value is sent as `''`, which the
 *     funnel reads as "delete the key".
 *  3. `meeting_point`, `review`, `photo_sharing` and `pre_check_settings` are sent whole:
 *     the stored object with the touched keys overlaid. The meeting point's `photo` is
 *     sent only when touched (null clears it; a staged file rides as a multipart part,
 *     which the view attaches to this object).
 *  4. Collections are sent whole, because an omitted row means delete: the membership
 *     list's order, each row from its raw row with its entries overlaid, new rows
 *     without `id`. `index[collection]` lists the row ids in the order sent, so a
 *     positional 400 (`additional_custom_ids.2`) maps back to its record.
 *  5. The itinerary is the full rebuilt tree when the draft touches it (`itinerary/
 *     compile.ts`, with `index.days` / `index.slots` / `index.activities`). `start_date`
 *     travels alone: saving the tour recomputes `end_date` from the stored itinerary's day
 *     count, and from the new one when a day is added or removed in the same save.
 *  Also: `departures` is `[{tag: {id}}]`; `length` is sent as whole seconds when the draft
 *  sets a manual length (`length_is_manual` on, or the length edited), and
 *  `length_is_manual: false` alone when it goes back to the derived length;
 *  `context.waiver_required` is `true` or `''` (a falsy context value deletes the key);
 *  `pre_check_settings` drops the legacy `tag_categories_enabled` /
 *  `selected_tag_categories` mirrors once the lead booker's own keys are there.
 *  6. Pins: `slot_pins` (saved stops) and `slot_pins_by_pos` (new ones) — see there.
 *  7. Route files: `kml_day_<di>` parts, the tour-level `kml_file` part (B6).
 *  8. `expected_updated_ts` comes from the baseline (the latest loaded detail).
 *
 *  `included` lists the changes the body really carries: an entry of a saved row the
 *  server no longer has, of a record no collection knows, or a staged file this page no
 *  longer holds is left out — the receipt and the draft's SAVED step must not claim it.
 *
 *  Text values are trimmed; an empty text is null, except a context key (`''`). */
import type { TourUploadFiles, TourWriteInput } from '@/api/tours';
import { parseRecordKey, type DraftWireChange } from '@/features/records/draft/changeSet';
import { isStagedFile, type StagedFiles } from '@/features/records/fields/widgets/stagedFiles';

import type { TourBaseline } from './baselines';
import {
  DEPARTURES_PATH,
  FILE_PATHS,
  ID_PATHS,
  ID_SET_PATHS,
  TOUR_ENTITY,
  WAIVER_REQUIRED_PATH,
  WHOLE_OBJECT_KEYS,
  type WholeObjectKey,
} from './catalog';
import { compileItinerary, isItineraryChange, type ItineraryIndex } from '../itinerary/compile';

import { COLLECTION_BY_MEMBERSHIP, COLLECTIONS, type CollectionKey } from './collections';

export type TourPatchIndex = Record<CollectionKey | 'days', number[]> & Partial<Pick<ItineraryIndex, 'slots' | 'activities'>>;

export interface CompiledTourPatch {
  body: TourWriteInput;
  files: TourUploadFiles;
  index: TourPatchIndex;
  /** Staged files the draft names but this page no longer holds (after a reload). */
  missingFiles: Array<{ path: string; name: string }>;
  /** The changes the body carries (see above). */
  included: DraftWireChange[];
}

const WHOLE: ReadonlySet<string> = new Set(WHOLE_OBJECT_KEYS);

function clean(v: unknown): unknown {
  if (typeof v !== 'string') return v ?? null;
  const t = v.trim();
  return t === '' ? null : t;
}

function wireValue(path: string, to: unknown): unknown {
  if (ID_PATHS.has(path)) return to === null || to === undefined ? null : { id: to };
  if (ID_SET_PATHS.has(path)) return (Array.isArray(to) ? to : []).map((id) => ({ id }));
  return clean(to);
}

export function compileTourPatch(
  changes: readonly DraftWireChange[],
  base: TourBaseline,
  files?: StagedFiles,
): CompiledTourPatch {
  const body: TourWriteInput = {};
  const out: TourUploadFiles = {};
  const index: TourPatchIndex = { additional_custom_ids: [], tag_categories: [], pre_trip_items: [], days: [] };
  const missingFiles: CompiledTourPatch['missingFiles'] = [];

  const included: DraftWireChange[] = [];
  /** Row changes by record, kept until their row is known to be sent. */
  const rowChanges = new Map<string, DraftWireChange[]>();
  const objects = new Map<WholeObjectKey, Record<string, unknown>>();
  const context: Record<string, unknown> = {};
  let contextTouched = false;
  /** Row entries per record (`alias:12` → path → to). */
  const rowEntries = new Map<string, Record<string, unknown>>();
  /** Membership lists the draft changed (`additional_custom_ids.order` → ids). */
  const orders = new Map<string, number[]>();
  /** `length` / `length_is_manual` — compiled together after the loop. */
  const lengthChanges = new Map<string, DraftWireChange>();

  /** null: clear; undefined: no JSON value (a staged file rides as a part, or is
   *  missing); `false`: a staged file this page no longer holds — nothing is sent. */
  const stage = (path: string, to: unknown, onFile: (f: File) => void): unknown => {
    if (!isStagedFile(to)) return to === null || to === undefined ? null : undefined;
    const file = files?.get(to);
    if (file) onFile(file);
    else {
      missingFiles.push({ path, name: to.name });
      return false;
    }
    return undefined; // a staged file is a multipart part, never a JSON value
  };

  for (const c of changes) {
    const path = c.field ?? '';
    if (!path) continue;
    if (isItineraryChange(c, TOUR_ENTITY)) continue; // rules 5–7, below
    if (c.entity !== TOUR_ENTITY) {
      const rec = `${c.entity}:${c.id}`;
      rowEntries.set(rec, { ...(rowEntries.get(rec) ?? {}), [path]: c.to });
      rowChanges.set(rec, [...(rowChanges.get(rec) ?? []), c]);
      continue;
    }
    if (c.id !== base.tourId) continue;

    if (COLLECTION_BY_MEMBERSHIP.has(path)) {
      orders.set(path, Array.isArray(c.to) ? (c.to as number[]) : []);
      included.push(c);
      continue;
    }
    if (path === DEPARTURES_PATH) {
      body.departures = (Array.isArray(c.to) ? c.to : []).map((id) => ({ tag: { id } }));
      included.push(c);
      continue;
    }
    if (path === 'length' || path === 'length_is_manual') {
      lengthChanges.set(path, c);
      continue;
    }
    if (path === WAIVER_REQUIRED_PATH) {
      contextTouched = true;
      context.waiver_required = c.to === true ? true : '';
      included.push(c);
      continue;
    }
    const dot = path.indexOf('.');
    const head = dot > 0 ? path.slice(0, dot) : path;
    const tail = dot > 0 ? path.slice(dot + 1) : '';

    if (head === 'context' && tail) {
      contextTouched = true;
      const v = typeof c.to === 'string' ? c.to.trim() : c.to;
      context[tail] = v === null || v === undefined ? '' : v;
      included.push(c);
      continue;
    }
    if (WHOLE.has(head) && tail) {
      const key = head as WholeObjectKey;
      const obj = objects.get(key) ?? { ...(base.stored[key] ?? emptyObject(key)) };
      if (path === 'meeting_point.photo') {
        const v = stage(path, c.to, (f) => (out.meetingPointPhoto = f));
        if (v === null) obj.photo = null;
        if (v !== false) included.push(c);
      } else {
        obj[tail] = key === 'pre_check_settings' ? c.to : clean(c.to);
        included.push(c);
      }
      objects.set(key, obj);
      continue;
    }
    if (FILE_PATHS.has(path)) {
      // cover_image: a staged file is the `cover_image` part; null clears it.
      const v = stage(path, c.to, (f) => (out.coverImage = f));
      if (v === null) body.cover_image = null;
      if (v !== false) included.push(c);
      continue;
    }
    body[path] = wireValue(path, c.to);
    included.push(c);
  }

  if (contextTouched) body.context = context;
  const precheck = objects.get('pre_check_settings');
  if (precheck && 'lead_tag_categories_enabled' in precheck && 'lead_selected_tag_categories' in precheck) {
    delete precheck.tag_categories_enabled;
    delete precheck.selected_tag_categories;
  }
  for (const [key, obj] of objects) body[key] = obj;

  // ---- length: a manual length is a positive number of seconds; off = derived again.
  const manual = lengthChanges.get('length_is_manual');
  const len = lengthChanges.get('length');
  if (manual && manual.to === false) {
    body.length_is_manual = false;
    included.push(manual);
  } else if (manual || len) {
    const seconds = len ? len.to : base.paths[base.rec]?.length;
    if (typeof seconds === 'number' && Number.isInteger(seconds) && seconds > 0) {
      body.length = seconds;
      if (manual) included.push(manual);
      if (len) included.push(len);
    }
  }

  // ---- collections: sent whole when the list or any of its rows changed
  for (const spec of COLLECTIONS) {
    const rowsTouched = [...rowEntries.keys()].some((rec) => parseRecordKey(rec).entity === spec.entity);
    if (!orders.has(spec.membership) && !rowsTouched) continue;
    const order = orders.get(spec.membership) ?? [...base.raw[spec.key].keys()];
    const rows: Record<string, unknown>[] = [];
    for (const id of order) {
      const raw = id > 0 ? (base.raw[spec.key].get(id) ?? null) : null;
      // A saved row the server no longer has cannot be sent by id (drift reports it).
      if (id > 0 && !raw) continue;
      const rec = `${spec.entity}:${id}`;
      const values = { ...(raw ? spec.project(raw) : spec.defaults), ...(rowEntries.get(rec) ?? {}) };
      rows.push(spec.toWire(raw, values));
      index[spec.key].push(id);
      included.push(...(rowChanges.get(rec) ?? []));
    }
    body[spec.key] = rows;
  }
  // Entries of a record no collection spec knows (no catalog yet), or of a saved row
  // the server no longer has, are never sent — and not in `included`.

  // ---- the itinerary (rules 5–7)
  const itinerary = compileItinerary(changes, base, files);
  Object.assign(body, itinerary.body);
  Object.assign(out, itinerary.files);
  index.days = itinerary.index.days;
  index.slots = itinerary.index.slots;
  index.activities = itinerary.index.activities;
  included.push(...itinerary.included);
  missingFiles.push(...itinerary.missingFiles);

  body.expected_updated_ts = base.updatedTs;
  return { body, files: out, index, missingFiles, included };
}

function emptyObject(key: WholeObjectKey): Record<string, unknown> {
  switch (key) {
    case 'meeting_point':
      return { name: null, address: null, comment: null, latitude: null, longitude: null, maps_url: null };
    case 'review':
    case 'photo_sharing':
      return { name: null, url: null };
    case 'pre_check_settings':
      return {};
  }
}
